import { useState, useMemo, useEffect } from "react";
import {
  PhoneOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  DollarCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
  DownOutlined,
  UserOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { formatDateTime } from "../utils/orderHelpers";
import { getDomainFromLocalStorage } from "../../../utils/commonfunction";
import { useSignalR } from "../../../providers/SignalRProvider";
import profilePic from "../../../assets/images/customer.png";
import { copyToClipboard } from "../../../utils/commonfunction";
import OrderTimeline from "./OrderTimeline";
import AssignRiderModal from "./AssignRiderModal";
import TransferBranchModal from "./TransferBranchModal";
import DeliveryTimeAdjuster from "./DeliveryTimeAdjuster";
import { message } from "antd";
import PreviousOrdersModal from "./PreviousOrdersModal";
import WhatsAppMessageModal from "./WhatsAppMessageModal";

// Status Code Start Here.......................................
const ORDER_WORKFLOW = {
  'Pending': ['Confirmed', 'Cancel'],
  'Confirmed': ['Dispatch', 'Cancel'],
  'Dispatch': ['Delivered', 'Cancel'],
  'Delivered': [],
  'Cancel': []
};

const getStatusConfig = (status) => {
  const configs = {
    Pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '🟡' },
    Confirmed: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: '🔵' },
    Dispatch: { color: 'bg-purple-100 text-purple-800 border-purple-300', icon: '🟣' },
    Delivered: { color: 'bg-green-100 text-green-800 border-green-300', icon: '🟢' },
    Cancel: { color: 'bg-red-100 text-red-800 border-red-300', icon: '🔴' }
  };
  return configs[status] || configs.Pending;
};

const getActionButtonStyle = (action) => {
  const styles = {
    Confirmed: 'bg-blue-500 hover:bg-blue-600 text-white',
    Dispatch: 'bg-purple-500 hover:bg-purple-600 text-white',
    Delivered: 'bg-green-500 hover:bg-green-600 text-white',
    Cancel: 'bg-red-500 hover:bg-red-600 text-white'
  };
  return styles[action] || 'bg-gray-500 hover:bg-gray-600 text-white';
};
// Status Code End Here.......................................

export default function OrderDetailsModal({
  order,
  onClose,
  OrderStatuses,
  onRiderChange,
  onBranchTransfer,
  onDeliveryTimeUpdate,
  riders,
  branches,
}) {
  const { connection } = useSignalR();

  // Calculate payment summary from API values
  // const taxAmount = Math.round((order.AmountWithGst || 0) - (order.AmountWithoutGst || 0));
  const taxAmount = Math.round((order.GstPercentage / 100) * order.AmountWithoutGst);
  const discount = order.TotalDiscount;
  const subtotal = order.AmountWithoutGst;
  const deliveryCharges = order.DeliveryCharges;
  const grandTotal = Math.round((subtotal) + (deliveryCharges) + (taxAmount) - (discount));

  // whatsapp modal state
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);

  // order status
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(order.Status);

  // rider states
  const [isRiderModalOpen, setIsRiderModalOpen] = useState(false);
  const [isAssigningRider, setIsAssigningRider] = useState(false);
  const [assignedRider, setAssignedRider] = useState(null);

  // branch transfer states
  const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
  const [isTransferringBranch, setIsTransferringBranch] = useState(false);
  const [transferredBranchId, setTransferredBranchId] = useState(null);

  // delivery time adjustment states
  const [isUpdatingDeliveryTime, setIsUpdatingDeliveryTime] = useState(false);
  const [isDeliveryTimeModalOpen, setIsDeliveryTimeModalOpen] = useState(false);
  const [updatedDeliveryTime, setUpdatedDeliveryTime] = useState(null);

  // customer previous orderts
  const [isPreviousOrdersModalOpen, setIsPreviousOrdersModalOpen] = useState(false);

  // Add this handler function after other handlers
  const handleViewPreviousOrders = () => {
    if (!connection) return;

    const domain = getDomainFromLocalStorage();
    setIsPreviousOrdersModalOpen(true);

    try {
      connection.invoke(
        "CustomerOrderHistory",
        domain,
        order.OrderToken,
        "CustomerPreviousOrders"
      );
    } catch (err) {
      console.error("Error fetching previous orders:", err);
      message.error("Error loading previous orders. Please try again.");
    }
  };


  // Get Current Branch
  const currentBranch = useMemo(() => {
    if (transferredBranchId) {
      return branches?.find(b => b.Id === transferredBranchId);
    }

    if (order.BranchId) {
      return branches?.find(b => b.Id === order.BranchId) || { Id: order.BranchId, Name: order.BranchName };
    }
    return null;
  }, [transferredBranchId, branches, order.BranchId]);

  // Get current assigned rider
  const currentRider = useMemo(() => {
    return assignedRider || order.Rider;
  }, [assignedRider, order.Rider]);

  // Get current delivery time 
  const currentDeliveryTime = useMemo(() => {
    return updatedDeliveryTime !== null ? updatedDeliveryTime : order.DeliveryTime;
  }, [updatedDeliveryTime, order.DeliveryTime]);


  // Listen for status update response from SignalR (for loading state)
  useEffect(() => {
    if (!connection || connection.state !== "Connected") return;

    const handleStatusResponse = (response) => {

      if (response.orderToken !== order.OrderToken) return;

      if (response.dataPayload?.Success) {
        const newStatus = response.dataPayload.OrderStatusName;
        setCurrentStatus(newStatus);
        message.success(`Order ${order.OrderToken} status updated to ${newStatus}!`);
      } else {
        message.error("Failed to update status. Please try again.");
      }

      setUpdatingStatus(false);
    };

    connection.on("OrderStatusUpdate", handleStatusResponse);

    return () => {
      connection.off("OrderStatusUpdate", handleStatusResponse);
    };
  }, [connection, order.OrderToken]);

  // Sync local status when parent updates (cross-device updates)
  useEffect(() => {
    setCurrentStatus(order.Status);
  }, [order.Status]);



  const handleStatusUpdate = (statusName) => {
    if (!connection || updatingStatus) return;

    // Find the status ID from the status name
    const statusId = Object.keys(OrderStatuses).find(
      (key) => OrderStatuses[key] === statusName
    );

    if (!statusId) {
      console.error('Status ID not found for:', statusName);
      return;
    }

    const domain = getDomainFromLocalStorage();
    setUpdatingStatus(true);

    try {
      connection.invoke(
        "OrderStatus",
        domain,
        order.BranchId,
        order.OrderToken,
        Number(statusId),
        null,
        null,
        null,
        "OrderStatusUpdate",
      );
    } catch (err) {
      console.error("Error updating status:", err);
      setUpdatingStatus(false);
      message.error("Error updating status. Please try again.");
    }
  };

  const handleAssignRider = (riderId) => {
    if (!connection || !riderId) return;

    const domain = getDomainFromLocalStorage();
    setIsAssigningRider(true);

    connection.on("OnRiderAssign", (response) => {

      connection.off("OnRiderAssign");

      if (response.dataPayload?.Success) {
        message.success("Rider assigned!");
        setIsRiderModalOpen(false);

        // find rider based on rider id and send rider object to the funciton
        const rider = riders?.find(rider => rider.Id === riderId);
        if (rider) {
          setAssignedRider(rider);
          onRiderChange(order.OrderNumber, rider);
        }
      } else {
        message.error("Failed to assign rider. Please try again.");
      }

      setIsAssigningRider(false);
    });

    try {
      connection.invoke(
        "OrderStatus",
        domain,
        order.BranchId,
        order.OrderToken,
        null, // order status id
        null, // branch id
        riderId, // rider id
        null, // delivery time incr or dec 
        "OnRiderAssign"
      );
    } catch (err) {
      console.error("Error assigning rider:", err);
      connection.off("OnRiderAssign");
      setIsAssigningRider(false);
      message.error("Error assigning rider. Please try again.");
    }
  };

  const handleTransferBranch = (branchId) => {
    if (!connection || !branchId) return;

    const domain = getDomainFromLocalStorage();
    setIsTransferringBranch(true);

    connection.on("OnBranchTransfer", (response) => {

      connection.off("OnBranchTransfer");

      if (response.dataPayload?.Success) {
        message.success("Order transferred successfully!");
        onBranchTransfer(order.OrderNumber, branchId);
        setIsBranchModalOpen(false);
        setTransferredBranchId(branchId);
      } else {
        message.error("Failed to transfer order. Please try again.");
      }

      setIsTransferringBranch(false);
    });

    try {
      connection.invoke(
        "OrderStatus",
        domain,
        order.BranchId,
        order.OrderToken,
        null, // order status id
        branchId, // branch id
        null, // rider id
        null, // delivery time incr or dec 
        "OnBranchTransfer"
      );
    } catch (err) {
      connection.off("OnBranchTransfer");
      setIsTransferringBranch(false);
      message.error("Error transferring order. Please try again.");
    }
  };

  const handleDeliveryTimeUpdate = (adjustmentMinutes) => {
    if (!connection || adjustmentMinutes === 0) return;

    const domain = getDomainFromLocalStorage();
    setIsUpdatingDeliveryTime(true);

    connection.on("DeliveryTimeUpdate", (response) => {


      connection.off("DeliveryTimeUpdate");

      if (response.dataPayload?.Success) {

        console.log("Checking Delivery Time Update ............................ ", response)

        message.success(`Delivery time ${adjustmentMinutes > 0 ? 'delayed' : 'advanced'} by ${Math.abs(adjustmentMinutes)} minutes!`);
        setIsDeliveryTimeModalOpen(false);

        const newTime = order.DeliveryTime + adjustmentMinutes;
        setUpdatedDeliveryTime(newTime);

        onDeliveryTimeUpdate(order.OrderNumber, adjustmentMinutes);
      } else {
        message.error("Failed to update delivery time. Please try again.");
      }

      setIsUpdatingDeliveryTime(false);
    });

    try {
      connection.invoke(
        "OrderStatus",
        domain,
        order.BranchId,
        order.OrderToken,
        null, // order status id
        null, // branch id
        null, // rider id
        adjustmentMinutes, // delivery time incr or dec +5 or -5 in int
        "DeliveryTimeUpdate"
      );
    } catch (err) {
      console.error("Error updating delivery time:", err);
      connection.off("DeliveryTimeUpdate");
      setIsUpdatingDeliveryTime(false);
      message.error("Error updating delivery time. Please try again.");
    }
  };


  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md p-4" style={{ zIndex: 100 }}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[98vh] overflow-hidden"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="relative px-6 py-4 border-b">
          <button
            onClick={onClose}
            className="absolute top-[26px] right-6 w-9 h-9 flex items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-gray-800"
          >
            <CloseOutlined className="" />
          </button>

          <div className="flex items-center justify-between pr-12">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                View Order
              </h2>
              <p className="text-sm text-gray-500 mt-1 font-mono">
                {order.OrderToken}
              </p>
            </div>

            {/* Status Badge + Action Buttons */}
            {ORDER_WORKFLOW[currentStatus]?.length > 0 ? (
              // Has actions - show full layout
              <div className="flex items-center gap-4">
                {/* Current Status */}
                <div className="flex flex-col items-start">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Current Status</span>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 font-semibold text-sm ${getStatusConfig(currentStatus).color}`}>
                    <span>{getStatusConfig(currentStatus).icon}</span>
                    <span>{currentStatus}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-12 w-px bg-gray-300"></div>

                {/* Action Buttons */}
                <div className="flex flex-col items-start min-w-[200px]">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Next Action</span>

                  <div className="flex items-center gap-2 min-h-[34px]">
                    {updatingStatus ? (
                      <div className="flex items-center gap-2 text-blue-500">
                        <LoadingOutlined className="text-lg" />
                        <span className="text-sm font-medium">Updating...</span>
                      </div>
                    ) : (
                      ORDER_WORKFLOW[currentStatus]?.map((action) => (
                        <button
                          key={action}
                          onClick={() => handleStatusUpdate(action)}
                          disabled={updatingStatus}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${getActionButtonStyle(action)}`}
                        >
                          {action}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // No actions - final state - show badge only (near close button)
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 font-semibold text-sm ${getStatusConfig(currentStatus).color}`}>
                <span>{getStatusConfig(currentStatus).icon}</span>
                <span>{currentStatus}</span>
              </div>
            )}

          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(98vh-100px)]">
          {/* grid columns */}
          <div className="grid grid-cols-4 gap-4">

            {/* Customer Related Information */}
            <div className="border rounded-xl p-4">
              {/* pic, name and prevecious orders */}
              <div className="flex flex-col items-center  mb-3  border-gray-100">
                <div className="mb-2 w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full flex items-center justify-center text-white text-5xl font-bold">
                  {order.CustomerDetails?.FullName?.charAt(0).toUpperCase()}
                </div>

                <h1 className="text-2xl font-bold text-gray-800 flex items-center ">
                  {order.CustomerDetails?.Title?.toUpperCase()} {order.CustomerDetails?.FullName?.toUpperCase()}
                </h1>

                {
                  order.CustomerDetails?.EmailAddress && (
                    <p className="text-gray-500 text-sm">{order.CustomerDetails?.EmailAddress}</p>
                  )
                }

                {/* Previous Orders Section */}
                <div className="mt-2">
                  {order?.PreviousOrderCount > 0 ? (
                    <div className="">

                      {/* View Button */}
                      <button
                        onClick={handleViewPreviousOrders}
                        className="w-full bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <HistoryOutlined />
                        View Order History ({order?.PreviousOrderCount})
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-sm">
                      <span className="text-2xl">⭐</span>
                      <span className="text-white font-bold text-sm">New Customer</span>
                    </div>
                  )}
                </div>

              </div>

              {/* contact number, alt number, area branch and city name, landmark */}
              <div className="space-y-2.5">
                <InfoItem
                  icon={<PhoneOutlined className="rotate-90" />}
                  label="Phone"
                  value={order.CustomerDetails?.MobileNumber}
                  copyable
                />

                {/* WhatsApp Button */}
                {order.CustomerDetails?.MobileNumber && (
                  <button
                    onClick={() => setIsWhatsAppModalOpen(true)}
                    className="w-full mt-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 py-2.5 rounded-lg text-sm font-semibold shadow-md transition-all hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <WhatsAppOutlined className="text-lg" />
                    Send WhatsApp Message
                  </button>
                )}

                {/* Delivery Address */}
                {order.OrderType === 'Delivery' && (
                  <div className="border border-blue-100 bg-blue-50 rounded-xl p-2 mt-2">
                    <div className="text-blue-600 flex items-center gap-2 font-semibold">
                      <EnvironmentOutlined className="text-lg" />
                      <span>Delivery Address</span>
                    </div>

                    <div className="text-gray-700 text-base flex items-center justify-between gap-2 break-words">
                      <span className="flex-1">
                        {order.CustomerDetails?.DeliveryAddress || "N/A"}
                      </span>

                      {order.CustomerDetails?.DeliveryAddress && (
                        <button
                          onClick={() =>
                            copyToClipboard(
                              order.CustomerDetails?.DeliveryAddress,
                            )
                          }
                          className="text-blue-500 hover:text-blue-700 transition"
                        >
                          <CopyOutlined className="text-lg" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {order.CustomerDetails?.AlternateMobileNumber && (
                  <InfoItem
                    label="Alt. Phone"
                    value={order.CustomerDetails.AlternateMobileNumber}
                  />
                )}
                <InfoItem
                  label="Branch Name"
                  value={order?.BranchName || "No Branch"}
                />

                {order.OrderType === 'Delivery' ? (
                  <>
                    <InfoItem
                      label="Area Name"
                      value={order?.AreaName || "No Area"}
                    />
                    <InfoItem
                      label="City Name"
                      value={order?.CityName || "No City"}
                    />
                    <InfoItem
                      label="Landmark"
                      value={order.CustomerDetails?.NearestLandmark}
                    />
                  </>
                ) : (
                  // Pickup Order Indicator
                  <div className="mt-3 p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                        <ShopOutlined className="text-white text-xl" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-orange-800 font-bold text-lg mb-1">Pickup Order</p>
                      <p className="text-orange-600 text-sm font-medium">
                        Customer will collect from {order.BranchName || 'store'} Branch
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Inofrmation, Order Items, Actions Button for Assign Rider and Delivery Time */}
            <div className="col-span-2">
              {/* Order Information */}
              <div className="border rounded-xl p-4">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">
                  Order Information
                </h3>
                <div className="w-full flex items-center justify-between">
                  <InfoItem
                    label="Order Time"
                    value={formatDateTime(order.OrderTime)}
                  />
                  <div className="h-6 w-0.5 bg-gray-400"></div>
                  <InfoItem
                    label="Order Mode"
                    value={`${order.OrderType || ""}`}
                  />
                  <div className="h-6 w-0.5 bg-gray-400"></div>
                  <InfoItem
                    label="Order Source"
                    value={order.OrderSource || "Website"}
                  />

                  {
                    order.CustomerDetails.ChangeAmount > 0 && (
                      <><div className="h-6 w-0.5 bg-gray-400"></div>
                        <InfoItem
                          label="Change Amount"
                          value={`Rs. ${order.CustomerDetails.ChangeAmount.toFixed(2)}`}
                        />
                      </>
                    )
                  }
                </div>

                {order.CustomerDetails?.DeliveryInstructions && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-xs text-amber-700 font-medium mb-1">
                      Instructions
                    </p>
                    <p className="text-xs text-amber-900">
                      {order.CustomerDetails.DeliveryInstructions}
                    </p>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className=" rounded-xl mt-4">
                <div>
                  {/* <h3 className="text-base font-semibold mb-4 text-gray-900">
                    Order Items ({order.Items?.length || 0})
                  </h3> */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                            Item Details
                          </th>
                          <th className="px-4 py-4 text-center text-xs font-semibold text-gray-700 uppercase">
                            Quantity
                          </th>
                          <th className="px-4 py-4 text-right text-xs font-semibold text-gray-700 uppercase">
                            Unit Price
                          </th>
                          <th className="px-4 py-4 text-right text-xs font-semibold text-gray-700 uppercase">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {order.Items?.map((item, index) => (
                          <ItemRow key={index} item={item} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Actions Button For Assign Rider and Delivery Time */}
              <div className="mt-4 border rounded-xl p-4 bg-gradient-to-br from-slate-50 to-white">
                <div className="grid grid-cols-2 gap-3">
                  {/* Assign Rider Section */}
                  <div>

                    <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                          {currentRider?.Name?.charAt(0).toUpperCase() || 'R'}
                        </div>
                        <div>
                          <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">
                            Assigned Rider
                          </p>
                          <p className="text-sm font-bold text-blue-900">
                            {currentRider?.Name || 'No Rider Assigned'}
                          </p>
                        </div>
                      </div>
                    </div>


                    <button
                      onClick={() => setIsRiderModalOpen(true)}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-md transition-all hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <UserOutlined />
                      {currentRider ? 'Change Rider' : 'Assign Rider'}
                    </button>
                  </div>

                  {/* Transfer Branch Section */}
                  <div>

                    <div className="mb-3 p-3 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                          <ShopOutlined className="text-lg" />
                        </div>
                        <div>
                          <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">
                            Current Branch
                          </p>
                          <p className="text-sm font-bold text-green-900">
                            {currentBranch?.Name || 'No Branch Assigned'}
                          </p>
                        </div>
                      </div>
                    </div>


                    <button
                      onClick={() => setIsBranchModalOpen(true)}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-md transition-all hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <ShopOutlined />
                      Transfer Branch
                    </button>
                  </div>


                </div>
              </div>

            </div>

            {/* Order Time Line, Payment Summary, Previous Orders */}
            <div className="">
              {/* Order Time LIne */}
              <OrderTimeline
                orderStatusLogs={order?.OrderStatusLogs}
                orderStatuses={OrderStatuses}
              />

              {/* Payment Summary */}
              <div className="w-full max-w-sm mt-4 border rounded-xl p-4 bg-gray-800">
                <span className="w-full pb-2 flex items-center">
                  <DollarCircleOutlined
                    size={30}
                    className="text-gray-300 mr-2"
                  />
                  <h3 className="font-semibold text-lg text-white">
                    Payment Summary
                  </h3>
                </span>

                <div className="space-y-2.5">
                  <SummaryRow label="Subtotal" value={subtotal} />

                  {taxAmount > 0 && (
                    <SummaryRow
                      label={`Tax (${order.GstPercentage}%)`}
                      value={taxAmount}
                    />
                  )}

                  {discount > 0 && (
                    <SummaryRow label="Discount" value={discount} isDiscount />
                  )}

                  {deliveryCharges > 0 && (
                    <SummaryRow
                      label="Delivery Charges"
                      value={deliveryCharges}
                    />
                  )}

                  <div className="border-t-2 border-dashed border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-100">
                        Grand Total
                      </span>
                      <span className="text-2xl font-bold text-gray-100">
                        Rs. {grandTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <span>
                    {order.PaymentType && (
                      <span className="flex items-center justify-between w-full border-t mt-2 pt-2">
                        <span className="text-sm text-gray-200">
                          Payment Type
                        </span>
                        <span className="text-sm px-2 py-0.5 rounded-full bg-gray-300 text-gray-800">
                          {order.PaymentType}
                        </span>
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* Delivery Time Section */}
              {order.OrderType === 'Delivery' && (
                <div className="mt-4">
                  <div className="mb-3 p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center">
                        <ClockCircleOutlined style={{ fontSize: '18px' }} />
                      </div>
                    </div>
                    <p className="text-[12px] text-slate-500 font-semibold uppercase tracking-wide mb-2">
                      Delivery Time
                    </p>
                    <p className="text-xl font-bold text-slate-900 mb-1">
                      {currentDeliveryTime ? `${currentDeliveryTime} min` : "Not Set"}
                    </p>

                    <button
                      onClick={() => setIsDeliveryTimeModalOpen(true)}
                      className="w-full bg-slate-800 hover:bg-slate-900 text-white px-3 py-2.5 rounded-lg text-xs font-semibold shadow-sm transition-all hover:shadow-md flex items-center justify-center gap-2"
                    >
                      <ClockCircleOutlined />
                      Change Delivery Time
                    </button>
                  </div>
                </div>
              )}

              {/* Previous Orders */}
              <div className="mt-4">

              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Assign Rider Modal */}
      {isRiderModalOpen && (
        <AssignRiderModal
          riders={riders || []}
          onClose={() => setIsRiderModalOpen(false)}
          onAssign={handleAssignRider}
          isAssigning={isAssigningRider}
          currentRider={currentRider}
        />
      )}

      {/* Delivery Time Modal */}
      {isDeliveryTimeModalOpen && (
        <DeliveryTimeAdjuster
          currentDeliveryTime={currentDeliveryTime || 0}
          onClose={() => setIsDeliveryTimeModalOpen(false)}
          onUpdate={handleDeliveryTimeUpdate}
          isUpdating={isUpdatingDeliveryTime}
        />
      )}

      {/* Transfer Branch Modal */}
      {isBranchModalOpen && (
        <TransferBranchModal
          branches={branches || []}
          onClose={() => setIsBranchModalOpen(false)}
          onTransfer={handleTransferBranch}
          isTransferring={isTransferringBranch}
          currentBranchId={transferredBranchId || order.BranchId}
        />
      )}

      {/* Previous Orders Modal */}
      {isPreviousOrdersModalOpen && (
        <PreviousOrdersModal
          orderToken={order.OrderToken}
          onClose={() => setIsPreviousOrdersModalOpen(false)}
          connection={connection}
        />
      )}

      {/* WhatsApp Message Modal */}
      {isWhatsAppModalOpen && (
        <WhatsAppMessageModal
          order={order}
          onClose={() => setIsWhatsAppModalOpen(false)}
          currentRider={currentRider}
        />
      )}

    </div>
  );
}










































/* ================= ITEM ROW COMPONENT ================= */
function ItemRow({ item }) {
  const variation = item.Variations?.[0];
  const basePrice = variation?.Price || 0;

  let addonsPrice = 0;
  const allAddons = [];

  if (variation?.ItemChoices) {
    variation.ItemChoices.forEach((choice) => {
      if (choice.ItemOptions) {
        choice.ItemOptions.forEach((option) => {
          allAddons.push({
            name: option.Name,
            price: option.Price || 0,
            quantity: option.Quantity || 1,
          });
          addonsPrice += option.Price * (option.Quantity || 1);
        });
      }
    });
  }

  const itemTotalPerUnit = basePrice + addonsPrice;
  const itemTotal = itemTotalPerUnit * item.Quantity;

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-2">
        <div>
          <p className="font-semibold text-gray-900 text-sm mb-1">
            {item.Name}
          </p>
          <div className="flex gap-2 mb-2">
            {variation?.Size?.Name && variation.Size.Name !== "-" && (
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                {variation.Size.Name}
              </span>
            )}
            {variation?.Flavour?.Name && variation.Flavour.Name !== "-" && (
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                {variation.Flavour.Name}
              </span>
            )}
          </div>
          {allAddons.length > 0 && (
            <div className="mt-2 space-y-1">
              {allAddons.map((addon, idx) => (
                <div
                  key={idx}
                  className="text-xs text-gray-600 flex items-center gap-1"
                >
                  <span>+</span>
                  <span className="text-blue-500">
                    {addon.quantity > 1 ? `(${addon.quantity}x)` : ""}
                  </span>
                  <span>{addon.name}</span>
                  {addon.price > 0 && (
                    <span className="text-emerald-600 font-medium">
                      (Rs. {addon.price})
                    </span>
                  )}
                  {addon.price === 0 && (
                    <span className="text-gray-400">(Free)</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {item.Comment && (
            <p className="text-xs text-gray-500 italic mt-2">
              Note: {item.Comment}
            </p>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-center">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded font-bold text-gray-900">
          {item.Quantity}
        </span>
      </td>
      <td className="px-4 py-2 text-right">
        <div className="text-sm font-medium text-gray-900">
          {addonsPrice > 0
            ? `Base: Rs. ${basePrice.toLocaleString()} + Rs. ${addonsPrice}`
            : `Rs. ${basePrice.toLocaleString()}`}
        </div>
      </td>
      <td className="px-4 py-2 text-right">
        <span className="font-bold text-gray-900 text-base">
          Rs. {itemTotal.toLocaleString()}
        </span>
      </td>
    </tr>
  );
}

/* ================= INFO ITEM COMPONENT ================= */
function InfoItem({ icon, label, value, copyable = false }) {

  return (
    <div className="flex items-start gap-2 group">
      <div className="text-gray-400 text-sm mt-0.5">{icon}</div>

      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>

        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-gray-900 font-medium break-words">
            {value || "N/A"}
          </p>

          {copyable && value && (
            <button
              onClick={() => copyToClipboard(value)}
              className="text-gray-400 hover:text-black transition"
            >
              <CopyOutlined />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= SUMMARY ROW COMPONENT ================= */
function SummaryRow({ label, value, isDiscount = false }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-gray-200">{label}</span>
      <span
        className={`font-semibold ${isDiscount ? "text-red-100" : "text-gray-200"}`}
      >
        {isDiscount && "- "}Rs. {value.toLocaleString()}
      </span>
    </div>
  );
}
